import { useState } from "react";
import { useCorpusScoreboard, type CorpusScoreboard } from "../../hooks/useCorpusScoreboard";
import { useScoreboardConfig, useComputedMetrics, type ComputedMetric } from "../../hooks/useScoreboard";
import ScoreboardConfigurator from "./ScoreboardConfigurator";
import ScoreboardEvidenceDrawer from "./ScoreboardEvidenceDrawer";

interface Props {
  caseId: string;
}

/**
 * CorpusScoreboard — A.3 v0 panel. Styled to match the war-room background
 * art: dark bronze-amber parchment scrolled and pinned with a battle-worn
 * Viking seax. Heavy ember firelight underglow (the war-room torches are
 * lighting it from below), restrained cyan rim glow (the data layer is
 * still alive in the background but subordinate to the fire).
 *
 * Outer panel header keeps the war-room HUD framing. Inside, the artifact.
 *
 * v0 data caveat: per-message timestamps are synthetic from OCR ingest
 * (sequential 1-sec stamps inside a burst). Burst boundaries are real
 * wall-clock — so message counts, active days, and inter-burst gaps are
 * sound. Within-burst response times are not shown.
 */
function CorpusScoreboard({ caseId }: Props) {
  const { data, isLoading, error } = useCorpusScoreboard(caseId);
  const { data: specs } = useScoreboardConfig(caseId);
  const configured = (specs?.length ?? 0) > 0;
  const { data: customMetrics, isLoading: cLoading } = useComputedMetrics(caseId, specs);
  const [cfgOpen, setCfgOpen] = useState(false);
  const [evMetric, setEvMetric] = useState<ComputedMetric | null>(null);

  let body: React.ReactNode;
  let status: string;

  if (configured) {
    if (cLoading) {
      body = <ScrollFrame><Loading /></ScrollFrame>;
      status = "tallying the field";
    } else {
      body = <ScrollFrame><ConfiguredMetrics metrics={customMetrics ?? []} onSelect={setEvMetric} /></ScrollFrame>;
      status = `${customMetrics?.length ?? 0} custom`;
    }
  } else if (isLoading) {
    body = <ScrollFrame><Loading /></ScrollFrame>;
    status = "tallying the field";
  } else if (error) {
    body = <ScrollFrame><ErrorState message={error.message} /></ScrollFrame>;
    status = "the count is lost";
  } else if (!data || data.empty) {
    body = <ScrollFrame><EmptyState reason={data?.reason} /></ScrollFrame>;
    status = "no corpus";
  } else {
    body = (
      <ScrollFrame>
        <ScoreboardBody data={data} />
      </ScrollFrame>
    );
    status = `${data.actors?.length ?? 0} actors // ${data.window?.total_days ?? 0}d window`;
  }

  return (
    <div className="relative flex flex-col rounded border border-steel-dark bg-iron/70 backdrop-blur-sm">
      <PanelHeader label="scoreboard" status={status} onConfigure={() => setCfgOpen(true)} />
      <div className="flex-1 overflow-hidden p-5">{body}</div>
      {caseId ? (
        <ScoreboardConfigurator caseId={caseId} open={cfgOpen} onClose={() => setCfgOpen(false)} />
      ) : null}
      {caseId ? (
        <ScoreboardEvidenceDrawer caseId={caseId} metric={evMetric} onClose={() => setEvMetric(null)} />
      ) : null}
    </div>
  );
}

function PanelHeader({ label, status, onConfigure }: { label: string; status: string; onConfigure?: () => void }) {
  return (
    <div className="flex items-center justify-between border-b border-steel-dark/70 px-5 py-3 font-display text-base font-light uppercase tracking-[0.16em]">
      <span className="text-bone-dim">{label}</span>
      <div className="flex items-center gap-3">
        <span className="text-bone-mute/70">// {status}</span>
        {onConfigure ? (
          <button type="button" onClick={onConfigure} className="text-bone-mute transition-colors hover:text-ember-bright" aria-label="configure scoreboard">
            ⚙ configure
          </button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * ScrollFrame — bronze-amber cured-hide scroll, charred edges, fire-lit
 * from below, pinned with the seax through the top center.
 */
function ScrollFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative">
      {/* Restrained cyan halo — sits BEHIND, very subtle, the data layer */}
      <div
        className="scroll-halo-pulse pointer-events-none absolute -inset-2 z-0 rounded"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(95, 200, 255, 0.10) 0%, rgba(95, 200, 255, 0.04) 50%, transparent 75%)",
          filter: "blur(12px)",
        }}
      />

      {/* Dominant ember/firelight halo — large, warm, coming from below */}
      <div
        className="pointer-events-none absolute -inset-x-4 -bottom-3 top-1/2 z-0 rounded"
        style={{
          background:
            "radial-gradient(ellipse at bottom, rgba(220, 90, 25, 0.32) 0%, rgba(160, 50, 15, 0.16) 35%, transparent 70%)",
          filter: "blur(14px)",
        }}
      />

      {/* Knife — taller, positioned higher, the blade drives down through */}
      <SeaxKnife className="pointer-events-none absolute left-1/2 top-[-32px] z-30 -translate-x-1/2" />

      {/* Scroll body — dark bronze-amber cured hide */}
      <div
        className="relative z-10 overflow-hidden"
        style={{
          background: [
            // Base: deep bronze with warmer center
            "radial-gradient(ellipse at center, #4a3520 0%, #3a2818 50%, #2a1d10 100%)",
            // Burnt edge vignette
            "radial-gradient(ellipse at center, transparent 60%, rgba(20, 10, 4, 0.85) 100%)",
          ].join(", "),
          backgroundBlendMode: "normal, multiply",
          border: "1px solid #1a0e05",
          borderRadius: "2px",
          boxShadow: [
            // Inner deep shadow — vignette
            "inset 0 0 80px rgba(15, 8, 3, 0.85)",
            "inset 0 0 20px rgba(15, 8, 3, 0.6)",
            // Subtle inner rim highlight at top (where torchlight hits)
            "inset 0 2px 1px rgba(180, 130, 60, 0.25)",
            // Restrained cyan tech rim — barely there
            "0 0 18px rgba(95, 200, 255, 0.12)",
            // Dominant ember firelight glow
            "0 6px 36px rgba(200, 80, 25, 0.35)",
            "0 0 12px rgba(220, 100, 30, 0.25)",
            // Hard drop shadow — heavy on the wall
            "0 8px 24px rgba(0, 0, 0, 0.75)",
          ].join(", "),
        }}
      >
        {/* Layered grain/stain overlays — splotches, fiber lines, wear */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background: [
              // Darker stains scattered
              "radial-gradient(circle at 18% 22%, rgba(10, 5, 2, 0.35) 0%, transparent 8%)",
              "radial-gradient(circle at 82% 78%, rgba(15, 8, 3, 0.40) 0%, transparent 12%)",
              "radial-gradient(circle at 55% 65%, rgba(8, 4, 1, 0.25) 0%, transparent 6%)",
              "radial-gradient(circle at 33% 88%, rgba(20, 10, 4, 0.30) 0%, transparent 9%)",
              // Subtle horizontal fiber striations (hide grain)
              "repeating-linear-gradient(90deg, transparent 0px, rgba(80, 50, 25, 0.04) 1px, transparent 3px, transparent 7px)",
            ].join(", "),
            mixBlendMode: "multiply",
            opacity: 0.9,
          }}
        />

        {/* Top scorched / curl shadow — much heavier, like the top edge is burnt */}
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-8 z-10"
          style={{
            background:
              "linear-gradient(to bottom, rgba(8, 4, 1, 0.90) 0%, rgba(8, 4, 1, 0.55) 35%, rgba(8, 4, 1, 0.15) 75%, transparent 100%)",
          }}
        />

        {/* Pin-puncture wound — heavier, slightly bloody/rusty */}
        <div
          className="pointer-events-none absolute z-10 h-4 w-14"
          style={{
            left: "50%",
            top: "0",
            transform: "translate(-50%, -3px)",
            background:
              "radial-gradient(ellipse, rgba(20, 5, 2, 0.85) 0%, rgba(80, 30, 12, 0.35) 50%, transparent 80%)",
          }}
        />

        {/* Bottom torched edge */}
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-6 z-10"
          style={{
            background:
              "linear-gradient(to top, rgba(8, 4, 1, 0.85) 0%, rgba(8, 4, 1, 0.45) 30%, rgba(8, 4, 1, 0.10) 70%, transparent 100%)",
          }}
        />

        {/* A few faint embers / sparks at the bottom, picking up the firelight */}
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-12 z-10"
          style={{
            background: [
              "radial-gradient(circle at 22% 95%, rgba(230, 100, 30, 0.6) 0%, transparent 4%)",
              "radial-gradient(circle at 67% 88%, rgba(220, 80, 20, 0.5) 0%, transparent 3%)",
              "radial-gradient(circle at 88% 92%, rgba(240, 130, 40, 0.45) 0%, transparent 2.5%)",
              "radial-gradient(circle at 12% 90%, rgba(220, 90, 25, 0.4) 0%, transparent 2%)",
            ].join(", "),
            filter: "blur(0.5px)",
          }}
        />

        {/* Content */}
        <div className="relative z-0 px-7 pb-9 pt-12">{children}</div>
      </div>
    </div>
  );
}

/**
 * SeaxKnife — heavier, more brutalist Viking seax. Darker patinated steel
 * with bronze undertones, battle nicks on the edge, iron rivets in the
 * handle wrap, weighty cast pommel. Less polished, more functional.
 */
function SeaxKnife({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 50 180"
      width="36"
      height="142"
      className={className}
      aria-hidden="true"
    >
      <defs>
        {/* Blade — dark patinated steel with bronze warmth in the highlights */}
        <linearGradient id="seax-blade-body" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stopColor="#0a0805" />
          <stop offset="0.15" stopColor="#22201c" />
          <stop offset="0.40" stopColor="#5a5248" />
          <stop offset="0.55" stopColor="#7a6f5e" />
          <stop offset="0.72" stopColor="#3e362c" />
          <stop offset="1" stopColor="#080604" />
        </linearGradient>
        {/* Vertical patina banding — uneven hammer-forge marks */}
        <linearGradient id="seax-patina" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#1a140c" stopOpacity="0.0" />
          <stop offset="0.15" stopColor="#0a0604" stopOpacity="0.55" />
          <stop offset="0.32" stopColor="#1a140c" stopOpacity="0.20" />
          <stop offset="0.48" stopColor="#080403" stopOpacity="0.50" />
          <stop offset="0.65" stopColor="#1a140c" stopOpacity="0.25" />
          <stop offset="0.85" stopColor="#080403" stopOpacity="0.60" />
          <stop offset="1" stopColor="#000000" stopOpacity="0.80" />
        </linearGradient>
        {/* Rust/blood undertone — barely visible, dark crimson in the shadows */}
        <linearGradient id="seax-rust" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stopColor="#3a0e04" stopOpacity="0.0" />
          <stop offset="0.5" stopColor="#5a1808" stopOpacity="0.18" />
          <stop offset="1" stopColor="#3a0e04" stopOpacity="0.0" />
        </linearGradient>
        {/* Subtle ember reflection along the edge — picks up the war-room firelight */}
        <linearGradient id="seax-emberglow" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stopColor="#d4602a" stopOpacity="0.0" />
          <stop offset="0.5" stopColor="#e87a3a" stopOpacity="0.45" />
          <stop offset="1" stopColor="#d4602a" stopOpacity="0.0" />
        </linearGradient>
        {/* Restrained cyan tech rim — much fainter than before */}
        <linearGradient id="seax-techglow" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stopColor="#5fc8ff" stopOpacity="0.0" />
          <stop offset="0.5" stopColor="#7fd4ff" stopOpacity="0.25" />
          <stop offset="1" stopColor="#5fc8ff" stopOpacity="0.0" />
        </linearGradient>
        {/* Handle — dark horn / wrapped leather */}
        <linearGradient id="seax-handle" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stopColor="#0a0502" />
          <stop offset="0.3" stopColor="#1f1006" />
          <stop offset="0.55" stopColor="#3a200d" />
          <stop offset="0.75" stopColor="#1a0a04" />
          <stop offset="1" stopColor="#050201" />
        </linearGradient>
        {/* Iron — pommel, guard, rivets — cast and pitted */}
        <linearGradient id="seax-iron" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stopColor="#0c0a08" />
          <stop offset="0.25" stopColor="#2a2620" />
          <stop offset="0.50" stopColor="#5a5248" />
          <stop offset="0.72" stopColor="#383028" />
          <stop offset="1" stopColor="#08060404" />
        </linearGradient>
        {/* Single rivet — small round iron stud */}
        <radialGradient id="seax-rivet" cx="0.35" cy="0.35" r="0.6">
          <stop offset="0" stopColor="#9a8e7a" />
          <stop offset="0.35" stopColor="#5a5248" />
          <stop offset="0.75" stopColor="#1a160f" />
          <stop offset="1" stopColor="#000000" />
        </radialGradient>
        <filter id="seax-drop" x="-30%" y="-10%" width="160%" height="120%">
          <feDropShadow dx="2" dy="3" stdDeviation="1.5" floodOpacity="0.70" />
        </filter>
        <filter id="seax-emberblur" x="-40%" y="-10%" width="180%" height="120%">
          <feGaussianBlur stdDeviation="1.6" />
        </filter>
      </defs>

      {/* Ember glow behind the blade — warm, from the firelight in the war room */}
      <path
        d="M 21 52 L 29 52 L 29 138 L 25 162 L 21 138 Z"
        fill="#d4602a"
        opacity="0.30"
        filter="url(#seax-emberblur)"
      />
      {/* Faint cyan tech glow behind — restrained */}
      <path
        d="M 21 52 L 29 52 L 29 138 L 25 162 L 21 138 Z"
        fill="#5fc8ff"
        opacity="0.10"
        filter="url(#seax-emberblur)"
      />

      <g filter="url(#seax-drop)">
        {/* Pommel — chunky cast iron block, slight Viking knot suggestion */}
        <path
          d="M 14 5 L 36 5 L 35 10 Q 25 13 15 10 Z"
          fill="url(#seax-iron)"
          stroke="#000"
          strokeWidth="0.4"
        />
        {/* Pommel band — small horizontal accent */}
        <rect x="14" y="10" width="22" height="2" fill="#0a0805" />
        <path d="M 14 5 L 36 5" stroke="#7a6f5e" strokeWidth="0.4" opacity="0.55" />

        {/* Handle body — dark horn / leather */}
        <path
          d="M 17 12 Q 16 30 17 48 L 33 48 Q 34 30 33 12 Z"
          fill="url(#seax-handle)"
          stroke="#000"
          strokeWidth="0.3"
        />
        {/* Leather wrap — irregular dark bands */}
        {[16, 21, 26, 32, 38, 44].map((y, i) => (
          <g key={y}>
            <path
              d={`M 17 ${y} Q 25 ${y + 1.4} 33 ${y}`}
              fill="none"
              stroke="#000"
              strokeWidth="0.5"
              opacity="0.85"
            />
            <path
              d={`M 17 ${y + 0.6} Q 25 ${y + 2.0} 33 ${y + 0.6}`}
              fill="none"
              stroke="#3a200d"
              strokeWidth="0.3"
              opacity="0.6"
            />
            {/* Rivets on alternating bands */}
            {i % 2 === 0 ? (
              <>
                <circle cx="19" cy={y + 0.8} r="0.7" fill="url(#seax-rivet)" />
                <circle cx="31" cy={y + 0.8} r="0.7" fill="url(#seax-rivet)" />
              </>
            ) : null}
          </g>
        ))}

        {/* Cross-guard — heavy iron, longer than handle, slight downward droop at ends */}
        <path
          d="M 8 47 L 42 47 L 41 51 L 32 52 Q 25 53 18 52 L 9 51 Z"
          fill="url(#seax-iron)"
          stroke="#000"
          strokeWidth="0.4"
        />
        <path d="M 8 47 L 42 47" stroke="#7a6f5e" strokeWidth="0.3" opacity="0.5" />

        {/* BLADE — heavier seax silhouette, slight asymmetry, long taper to point */}
        <path
          d="M 21 52
             L 29 52
             L 29 138
             Q 28.6 148 27.5 156
             L 25 162
             L 22.5 156
             Q 21.4 148 21 138
             Z"
          fill="url(#seax-blade-body)"
          stroke="#000"
          strokeWidth="0.3"
        />
        {/* Patina overlay — vertical hammer-forge banding */}
        <path
          d="M 21 52 L 29 52 L 29 138 L 25 162 L 21 138 Z"
          fill="url(#seax-patina)"
        />
        {/* Rust undertone */}
        <path
          d="M 21 52 L 29 52 L 29 138 L 25 162 L 21 138 Z"
          fill="url(#seax-rust)"
        />
        {/* Fuller — deep central channel */}
        <line x1="25" y1="58" x2="25" y2="148" stroke="#000" strokeWidth="0.55" opacity="0.7" />
        <line x1="25.5" y1="58" x2="25.5" y2="148" stroke="#7a6f5e" strokeWidth="0.22" opacity="0.4" />
        {/* Cutting edge (right) — narrow highlight + ember reflection */}
        <line x1="28.85" y1="55" x2="28.85" y2="142" stroke="#c2b6a0" strokeWidth="0.28" opacity="0.65" />
        <line x1="28.95" y1="60" x2="28.95" y2="140" stroke="url(#seax-emberglow)" strokeWidth="0.55" />
        {/* Spine (left) — heavy darken */}
        <line x1="21.15" y1="55" x2="21.15" y2="142" stroke="#000" strokeWidth="0.4" opacity="0.85" />
        {/* Subtle cyan tech kiss along the very edge — barely there */}
        <line x1="29.1" y1="65" x2="29.1" y2="135" stroke="url(#seax-techglow)" strokeWidth="0.35" />

        {/* Battle nicks on the cutting edge — small triangular wear marks */}
        <path d="M 28.5 78 L 29.4 78 L 28.95 80 Z" fill="#0a0805" />
        <path d="M 28.5 102 L 29.3 102 L 28.9 103.5 Z" fill="#0a0805" />
        <path d="M 28.5 124 L 29.4 124 L 28.95 126 Z" fill="#0a0805" />

        {/* Forge mark — small pitting on the blade */}
        <circle cx="23" cy="76" r="0.55" fill="#000" opacity="0.55" />
        <circle cx="26.5" cy="110" r="0.45" fill="#000" opacity="0.5" />
        <circle cx="23.5" cy="128" r="0.35" fill="#000" opacity="0.55" />

        {/* Dark streak — old blood / rust runoff down the fuller */}
        <path
          d="M 25 60 Q 24.5 90 25 130"
          stroke="#3a0e04"
          strokeWidth="0.4"
          opacity="0.45"
          fill="none"
        />
      </g>
    </svg>
  );
}

function ConfiguredMetrics({ metrics, onSelect }: { metrics: ComputedMetric[]; onSelect: (m: ComputedMetric) => void }) {
  if (metrics.length === 0) {
    return (
      <div className="flex h-32 items-center justify-center">
        <p className="font-display text-base font-light uppercase tracking-[0.16em]" style={{ color: "#cdb074" }}>
          // no metrics computed
        </p>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      {metrics.map((m, i) => {
        const d = (m.detail ?? {}) as Record<string, string>;
        return (
          <button
            type="button"
            key={`${m.primitive}-${i}`}
            onClick={() => onSelect(m)}
            className="px-4 py-3 text-left transition-colors hover:border-ember-bright/60"
            style={{
              background: "linear-gradient(to bottom, rgba(60, 42, 22, 0.55), rgba(35, 22, 10, 0.65))",
              border: "1px solid rgba(154, 120, 72, 0.35)",
              borderRadius: "2px",
              boxShadow: "inset 0 0 16px rgba(15, 8, 3, 0.7), inset 0 1px 0 rgba(180, 130, 60, 0.10)",
            }}
          >
            <ScrollLabel>{m.label}</ScrollLabel>
            <p className="mt-0.5 font-display text-3xl font-light" style={{ color: "#e8d4a8", textShadow: "0 1px 2px rgba(0,0,0,0.7)" }}>
              {m.value ?? "—"}
              <span className="ml-1.5 font-display text-base font-light uppercase tracking-[0.1em]" style={{ color: "#c4a86e" }}>
                {m.unit}{m.pct != null ? ` · ${m.pct}%` : ""}
              </span>
            </p>
            {d.first ? (
              <p className="mt-1 font-display text-base" style={{ color: "#cdbb98" }}>{d.first} → {d.last}</p>
            ) : d.direction ? (
              <p className="mt-1 font-display text-base" style={{ color: "#cdbb98" }}>{d.direction} {d.date}</p>
            ) : null}
            {m.why ? (
              <p className="mt-1.5 font-display text-sm leading-snug" style={{ color: "#b89f78" }}>{m.why}</p>
            ) : null}
            <p className="mt-2 font-display text-xs font-light uppercase tracking-[0.08em]" style={{ color: "#7aa3c9" }}>evidence ▸</p>
          </button>
        );
      })}
    </div>
  );
}

function ScoreboardBody({ data }: { data: CorpusScoreboard }) {
  return (
    <div className="flex flex-col gap-6">
      <WindowStrip window={data.window!} />
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <ActorsBlock actors={data.actors ?? []} totalDays={data.window?.total_days ?? 0} />
        <GapsBlock gaps={data.gaps!} />
      </div>
      <LongestSilences silences={data.longest_silences ?? []} />
    </div>
  );
}

function WindowStrip({ window }: { window: NonNullable<CorpusScoreboard["window"]> }) {
  return (
    <div
      className="flex flex-wrap items-baseline gap-x-6 gap-y-2 pb-3"
      style={{ borderBottom: "1px solid rgba(154, 120, 72, 0.28)" }}
    >
      <Stat label="window" value={`${window.start_date} → ${window.end_date}`} />
      <Stat label="span" value={`${window.total_days} days`} />
    </div>
  );
}

function ActorsBlock({
  actors,
  totalDays,
}: {
  actors: NonNullable<CorpusScoreboard["actors"]>;
  totalDays: number;
}) {
  return (
    <div>
      <ScrollHeading>// actors // {actors.length}</ScrollHeading>
      <div className="flex flex-col gap-3">
        {actors.map((a, idx) => {
          const accent = idx === 0 ? "ember" : "bronze";
          return (
            <ActorCard
              key={a.actor}
              actor={a.actor}
              messages={a.messages}
              pct={a.pct_of_corpus}
              activeDays={a.active_days}
              activePct={a.active_pct}
              totalDays={totalDays}
              accent={accent}
            />
          );
        })}
      </div>
    </div>
  );
}

function ActorCard({
  actor,
  messages,
  pct,
  activeDays,
  activePct,
  totalDays,
  accent,
}: {
  actor: string;
  messages: number;
  pct: number;
  activeDays: number;
  activePct: number;
  totalDays: number;
  accent: "ember" | "bronze";
}) {
  const barColor = accent === "ember" ? "#d4602a" : "#9a7848";
  const tagColor = accent === "ember" ? "#e08040" : "#b89868";

  return (
    <div
      className="px-4 py-3"
      style={{
        background:
          "linear-gradient(to bottom, rgba(60, 42, 22, 0.55), rgba(35, 22, 10, 0.65))",
        border: "1px solid rgba(154, 120, 72, 0.35)",
        borderRadius: "2px",
        boxShadow:
          "inset 0 0 16px rgba(15, 8, 3, 0.7), inset 0 1px 0 rgba(180, 130, 60, 0.10), 0 1px 2px rgba(0, 0, 0, 0.5)",
      }}
    >
      <div className="flex items-center justify-between gap-3">
        <p
          className="font-display text-base font-light"
          style={{ color: "#e0cfa8", textShadow: "0 1px 2px rgba(0,0,0,0.7)" }}
        >
          {actor}
        </p>
        <span
          className="shrink-0 px-2 py-0.5 font-display text-sm font-light uppercase tracking-[0.12em]"
          style={{
            color: tagColor,
            border: `1px solid ${tagColor}55`,
            background: "rgba(15, 8, 3, 0.55)",
            borderRadius: "2px",
          }}
        >
          {pct}% of corpus
        </span>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-4">
        <ScrollMetric
          label="messages"
          value={messages.toLocaleString()}
          barPct={pct}
          barColor={barColor}
        />
        <ScrollMetric
          label="active days"
          value={String(activeDays)}
          suffix={`/ ${totalDays}`}
          barPct={activePct}
          barColor={barColor}
        />
      </div>
    </div>
  );
}

function ScrollMetric({
  label,
  value,
  suffix,
  barPct,
  barColor,
}: {
  label: string;
  value: string;
  suffix?: string;
  barPct: number;
  barColor: string;
}) {
  return (
    <div>
      <ScrollLabel>{label}</ScrollLabel>
      <p
        className="mt-0.5 font-display text-2xl font-light"
        style={{ color: "#e8d4a8", textShadow: "0 1px 2px rgba(0,0,0,0.7)" }}
      >
        {value}
        {suffix ? (
          <span className="ml-1 font-display text-sm" style={{ color: "#c4a86e" }}>
            {suffix}
          </span>
        ) : null}
      </p>
      <div
        className="mt-2 h-1 w-full overflow-hidden"
        style={{
          background: "rgba(15, 8, 3, 0.65)",
          borderRadius: "1px",
          boxShadow: "inset 0 0 3px rgba(0,0,0,0.8)",
        }}
      >
        <div
          className="h-full"
          style={{
            width: `${barPct}%`,
            background: `linear-gradient(to right, ${barColor}cc, ${barColor})`,
            boxShadow: `0 0 6px ${barColor}88`,
          }}
        />
      </div>
    </div>
  );
}

function GapsBlock({ gaps }: { gaps: NonNullable<CorpusScoreboard["gaps"]> }) {
  return (
    <div>
      <ScrollHeading>// silent gaps between bursts // {gaps.n_gaps} gaps</ScrollHeading>
      <div className="grid grid-cols-2 gap-3">
        <GapStat label="median" value={formatGap(gaps.median_days)} />
        <GapStat label="mean" value={formatGap(gaps.mean_days)} />
        <GapStat label="p90" value={formatGap(gaps.p90_days)} />
        <GapStat label="max" value={formatGap(gaps.max_days)} accent="ember" />
      </div>
    </div>
  );
}

function GapStat({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: "ember";
}) {
  const color = accent === "ember" ? "#e8b078" : "#e8d4a8";
  return (
    <div
      className="px-3 py-2"
      style={{
        background:
          "linear-gradient(to bottom, rgba(60, 42, 22, 0.55), rgba(35, 22, 10, 0.65))",
        border: "1px solid rgba(154, 120, 72, 0.35)",
        borderRadius: "2px",
        boxShadow: "inset 0 0 12px rgba(15, 8, 3, 0.7), inset 0 1px 0 rgba(180, 130, 60, 0.08)",
      }}
    >
      <ScrollLabel>{label}</ScrollLabel>
      <p
        className="mt-0.5 font-display text-lg font-light"
        style={{ color, textShadow: "0 1px 2px rgba(0,0,0,0.7)" }}
      >
        {value}
      </p>
    </div>
  );
}

function LongestSilences({
  silences,
}: {
  silences: NonNullable<CorpusScoreboard["longest_silences"]>;
}) {
  if (silences.length === 0) return null;
  return (
    <div>
      <ScrollHeading>// longest silences // top {silences.length}</ScrollHeading>
      <div className="overflow-x-auto">
        <table className="w-full font-display text-base" style={{ color: "#d4c0a0" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid rgba(154, 120, 72, 0.4)", color: "#c4a86e" }}>
              <th className="py-1.5 pr-4 text-left font-normal uppercase tracking-[0.1em]">
                started
              </th>
              <th className="py-1.5 pr-4 text-left font-normal uppercase tracking-[0.1em]">
                resumed
              </th>
              <th className="py-1.5 pr-2 text-right font-normal uppercase tracking-[0.1em]">
                silent
              </th>
            </tr>
          </thead>
          <tbody>
            {silences.map((s, idx) => (
              <tr
                key={`${s.silence_started}-${idx}`}
                style={{
                  borderBottom:
                    idx === silences.length - 1
                      ? "none"
                      : "1px solid rgba(154, 120, 72, 0.15)",
                }}
              >
                <td className="py-1.5 pr-4" style={{ color: "#cdbb98" }}>
                  {s.silence_started}
                </td>
                <td className="py-1.5 pr-4" style={{ color: "#cdbb98" }}>
                  {s.silence_ended}
                </td>
                <td
                  className="py-1.5 pr-2 text-right"
                  style={{
                    color: "#e8d4a8",
                    textShadow: "0 1px 1px rgba(0,0,0,0.6)",
                  }}
                >
                  {formatGap(s.silent_days)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ScrollHeading({ children }: { children: React.ReactNode }) {
  return (
    <p
      className="mb-3 font-display text-base font-light uppercase tracking-[0.16em]"
      style={{ color: "#cdb074", textShadow: "0 1px 1px rgba(0,0,0,0.6)" }}
    >
      {children}
    </p>
  );
}

function ScrollLabel({ children }: { children: React.ReactNode }) {
  return (
    <p
      className="font-display text-sm font-light uppercase tracking-[0.12em]"
      style={{ color: "#c4a86e" }}
    >
      {children}
    </p>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <ScrollLabel>{label}</ScrollLabel>
      <p
        className="mt-0.5 font-display text-sm font-light"
        style={{ color: "#e8d4a8", textShadow: "0 1px 2px rgba(0,0,0,0.7)" }}
      >
        {value}
      </p>
    </div>
  );
}

function formatGap(days: number): string {
  if (days < 1 / 24) {
    return `${Math.round(days * 24 * 60)}m`;
  }
  if (days < 1) {
    return `${(days * 24).toFixed(1)}h`;
  }
  return `${days.toFixed(1)}d`;
}

function Loading() {
  return (
    <div className="flex h-32 items-center justify-center">
      <p
        className="flex items-center gap-3 font-display text-base font-light uppercase tracking-[0.16em]"
        style={{ color: "#cdb074" }}
      >
        <span className="inline-block h-1.5 w-1.5 animate-pulse-ember rounded-full" style={{ background: "#d4602a" }} />
        <span className="inline-block h-1.5 w-1.5 animate-pulse-ember rounded-full [animation-delay:200ms]" style={{ background: "#d4602a" }} />
        <span className="inline-block h-1.5 w-1.5 animate-pulse-ember rounded-full [animation-delay:400ms]" style={{ background: "#d4602a" }} />
        tallying the field
      </p>
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div>
      <p
        className="font-display text-base font-light uppercase tracking-[0.16em]"
        style={{ color: "#e08040" }}
      >
        // the count is lost
      </p>
      <p className="mt-2 font-display text-base" style={{ color: "#cdbb98" }}>
        {message}
      </p>
    </div>
  );
}

function EmptyState({ reason }: { reason?: string }) {
  return (
    <div className="flex h-32 items-center justify-center">
      <div className="max-w-md text-center">
        <p
          className="font-display text-base font-light uppercase tracking-[0.16em]"
          style={{ color: "#cdb074" }}
        >
          // no corpus on the field
        </p>
        <p className="mt-2 font-display text-sm" style={{ color: "#cdbb98" }}>
          {reason ?? "Bind an evidence corpus to this case to see the scoreboard."}
        </p>
      </div>
    </div>
  );
}

export default CorpusScoreboard;
